import React, { FC, RefObject, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableHighlight, View } from 'react-native';
import { CCToggleButton, PlayPauseButton, TWCText, VideoControls, VideoProgressIndicator } from '@components';
import { useRecoilValue } from 'recoil';

import { VODControlsTestIDs } from '@constants';
import { helpers } from '@helpers';
import { useAppContextReference, useNodeHandle } from '@hooks';
import { hdmiConnectedState, screenReaderEnabledState } from '@store';
import { VideoData } from '@types';

import { styles } from './styles';

import { Layout, tPath } from '../constants';

type OnDemandVideoControlsProps = {
  data: VideoData;
  nextFocusUp: number | null;
  nextFocusDown: number | null;
  playButtonReference: RefObject<number | null>;
  playPauseHandle: number | null;
  showControls: () => void;
  onControl: (control: string, value?: number) => void;
  onRef: (reference: { hidePlayList: (() => void) | null; focusPlayButton: () => void }) => void;
  onFocus: (() => void) | null;
};

export const OnDemandVideoControls: FC<OnDemandVideoControlsProps> = ({
  data,
  nextFocusUp,
  nextFocusDown,
  playButtonReference,
  playPauseHandle,
  showControls,
  onControl,
  onRef,
  onFocus,
}) => {
  const progressBarReference = useRef<number | null>(null);
  const ccButtonReference = useRef<number | null>(null);

  const hdmiConnected = useRecoilValue(hdmiConnectedState);
  const screenReaderEnabled = useRecoilValue(screenReaderEnabledState);

  const progressHandle = useNodeHandle(progressBarReference);
  const ccHandle = useNodeHandle(ccButtonReference);

  const controlsReference = useAppContextReference(VODControlsTestIDs.vodVideoControls);
  const controlsHandle = useNodeHandle(controlsReference);

  const hasGainedFocus = useRef(false);

  const { t } = useTranslation('translation', { keyPrefix: tPath });

  useEffect(() => {
    if (screenReaderEnabled && !hasGainedFocus.current && controlsHandle) {
      hasGainedFocus.current = true;
      helpers.FOCUS_NOW(controlsHandle);
    }
  }, [screenReaderEnabled, hasGainedFocus, controlsHandle]);

  useEffect(() => {
    // If hdmi device is disconnected or app is moved to background mode on Tizen device, the reproduction of video is paused
    if (!hdmiConnected) {
      onControl(VideoControls.PAUSE);
    } else if (helpers.isTizen) {
      // If the app come back from the background mode on Tizen device, resume the reproduction of video in video player
      onControl(VideoControls.PLAY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hdmiConnected]);

  useEffect(() => {
    onRef?.({
      hidePlayList: onFocus,
      focusPlayButton: () => helpers.FOCUS_NOW(playButtonReference),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onFocus]);

  const subtext =
    data.season && data.episode && data.title
      ? t('showSubtitle', {
          episode: data.episode,
          season: data.season,
          title: data.title,
        })
      : null;

  const isEpisode = data?.category?.toLowerCase() === 'full episode';

  return (
    <View style={styles.background}>
      <View style={styles.container}>
        <TWCText noTestID style={styles.textSpacing} variant={'mediumText48'}>
          {isEpisode ? data.seriestitle ?? data.title : data.title}
        </TWCText>
        <TWCText noTestID variant={'mediumText30Subheader'}>
          {subtext ? subtext : ''}
        </TWCText>
        <View style={styles.barContainer}>
          <VideoProgressIndicator
            duration={Number(data.duration)}
            height={Layout.vodProgressBarHeight}
            isLive={false}
            nextFocusDown={playPauseHandle}
            nextFocusLeft={screenReaderEnabled ? controlsHandle : progressHandle}
            nextFocusRight={screenReaderEnabled ? controlsHandle : progressHandle}
            nextFocusUp={nextFocusUp ?? screenReaderEnabled ? controlsHandle : progressHandle}
            progress={data.progress}
            ref={screenReaderEnabled ? controlsReference : progressBarReference}
            seekValue={data.seeking}
            showControls={showControls}
            width={Layout.vodProgressBarWidth}
            onControl={onControl}
          />
        </View>
        <View style={styles.buttonsContainer}>
          <PlayPauseButton
            accessibilityHint={`${t('pauseButtonAccessibilityHint')} ${
              nextFocusDown ? t('moreVideosAccessibilityHint') : ''
            }`}
            accessibilityHintActive={`${t('playButtonAccessibilityHint')} ${
              nextFocusDown ? t('moreVideosAccessibilityHint') : ''
            }`}
            accessibilityLabel={t('pauseButtonAccessibilityLabel')}
            accessibilityLabelActive={t('playButtonAccessibilityLabel')}
            nextFocusDown={nextFocusDown}
            nextFocusLeft={helpers.isAndroid ? playPauseHandle : null}
            nextFocusRight={ccHandle}
            nextFocusUp={screenReaderEnabled ? controlsHandle : progressHandle}
            paused={data.paused}
            ref={playButtonReference}
            showControls={showControls}
            testID={VODControlsTestIDs.playPauseButton}
            onControl={onControl}
            onFocus={onFocus}
          />
          <CCToggleButton
            accessibilityHint={`${t('ccButtonAccessibilityHintOff')} ${
              nextFocusDown ? t('moreVideosAccessibilityHint') : ''
            }`}
            accessibilityHintActive={`${t('ccButtonAccessibilityHintOn')} ${
              nextFocusDown ? t('moreVideosAccessibilityHint') : ''
            }`}
            accessibilityLabel={t('ccButtonAccessibilityLabel')}
            nextFocusDown={nextFocusDown}
            nextFocusLeft={playPauseHandle}
            nextFocusUp={screenReaderEnabled ? controlsHandle : progressHandle}
            ref={ccButtonReference}
            showControls={showControls}
            testID={VODControlsTestIDs.closedCaptionsButton}
          />
        </View>
      </View>
      <TouchableHighlight
        key={VODControlsTestIDs.vodVideoControls}
        activeOpacity={0}
        importantForAccessibility={'no'}
        nativeID={VODControlsTestIDs.vodVideoControls}
        nextFocusUp={nextFocusUp}
        ref={reference => {
          if (!hasGainedFocus.current && !screenReaderEnabled) {
            hasGainedFocus.current = true;
            helpers.FOCUS_NOW(reference);
          }
          if (!screenReaderEnabled) {
            controlsReference.current = reference;
          }
        }}
        style={styles.controlsNode}
        testID={VODControlsTestIDs.vodVideoControls}
        underlayColor={'clear'}
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onFocus={event => {
          helpers.FOCUS_NOW(screenReaderEnabled ? controlsReference : progressBarReference);
        }}
      >
        <View />
      </TouchableHighlight>
    </View>
  );
};
